import type {
  BoqRow,
  MatchCandidate,
  MatchingOptions,
  MatchResult,
  NumericTolerances,
  SimilarityBreakdown,
} from '../../types'
import {
  createEmptyAmountImpact,
  createEmptyFieldChanges,
  finalizeDiffResult,
  resolveTolerances,
  withEffectiveTotal,
} from '../diff'
import { normalizeItemCode, normalizeText, normalizeUnit } from '../normalization'
import { isComparableRow } from '../parsing'
import { buildCandidateIndex, searchFuzzyCandidates } from './candidates'
import { scoreRowSimilarity } from './similarity'

export interface ResolvedMatchingOptions {
  highConfidenceThreshold: number
  pendingThreshold: number
  ambiguityMargin: number
  maxCandidates: number
  maxDisplayedCandidates: number
  tolerances: NumericTolerances
}

export const DEFAULT_MATCHING_OPTIONS: Readonly<ResolvedMatchingOptions> = {
  highConfidenceThreshold: 0.88,
  pendingThreshold: 0.72,
  ambiguityMargin: 0.03,
  maxCandidates: 50,
  maxDisplayedCandidates: 3,
  tolerances: resolveTolerances(),
}

function bounded(value: number | undefined, fallback: number, minimum = 0, maximum = 1): number {
  return value !== undefined && Number.isFinite(value)
    ? Math.min(maximum, Math.max(minimum, value))
    : fallback
}

function positiveInteger(value: number | undefined, fallback: number): number {
  return value !== undefined && Number.isInteger(value) && value > 0 ? value : fallback
}

export function resolveMatchingOptions(options: MatchingOptions = {}): ResolvedMatchingOptions {
  const highConfidenceThreshold = bounded(
    options.highConfidenceThreshold,
    DEFAULT_MATCHING_OPTIONS.highConfidenceThreshold,
  )
  return {
    highConfidenceThreshold,
    pendingThreshold: Math.min(
      highConfidenceThreshold,
      bounded(options.pendingThreshold, DEFAULT_MATCHING_OPTIONS.pendingThreshold),
    ),
    ambiguityMargin: bounded(
      options.ambiguityMargin,
      DEFAULT_MATCHING_OPTIONS.ambiguityMargin,
      0,
      0.25,
    ),
    maxCandidates: positiveInteger(options.maxCandidates, DEFAULT_MATCHING_OPTIONS.maxCandidates),
    maxDisplayedCandidates: positiveInteger(
      options.maxDisplayedCandidates,
      DEFAULT_MATCHING_OPTIONS.maxDisplayedCandidates,
    ),
    tolerances: resolveTolerances(options.tolerances),
  }
}

function prepareRow(row: BoqRow): BoqRow {
  return withEffectiveTotal({
    ...row,
    normalizedFields: {
      itemCode: normalizeItemCode(row.itemCode),
      itemName: normalizeText(row.itemName),
      itemDescription: normalizeText(row.itemDescription),
      unit: normalizeUnit(row.unit),
    },
  })
}

function sortRows(rows: readonly BoqRow[]): BoqRow[] {
  return [...rows].sort(
    (left, right) =>
      left.sourceRowNumber - right.sourceRowNumber || left.id.localeCompare(right.id),
  )
}

function resultId(baselineRow: BoqRow | null, targetRow: BoqRow | null): string {
  return baselineRow === null
    ? `target:${targetRow?.id ?? 'missing'}`
    : `baseline:${baselineRow.id}`
}

function createResult(
  baselineRow: BoqRow | null,
  targetRow: BoqRow | null,
  matchType: MatchResult['matchType'],
  confidence: number,
  matchReason: string,
  confirmationState: MatchResult['confirmationState'],
  candidates: readonly MatchCandidate[],
  tolerances: Partial<NumericTolerances>,
): MatchResult {
  return finalizeDiffResult(
    {
      id: resultId(baselineRow, targetRow),
      baselineRow,
      targetRow,
      matchType,
      confidence,
      matchReason,
      confirmationState,
      status: 'pending',
      fieldChanges: createEmptyFieldChanges(),
      amountImpact: createEmptyAmountImpact(),
      candidates,
      manualDecision: null,
      preManualState: null,
    },
    tolerances,
  )
}

function groupByCode(rows: readonly BoqRow[]): Map<string, BoqRow[]> {
  const groups = new Map<string, BoqRow[]>()
  for (const row of rows) {
    const code = row.normalizedFields.itemCode
    if (!code) continue
    const group = groups.get(code)
    if (group === undefined) groups.set(code, [row])
    else group.push(row)
  }
  return groups
}

interface DuplicatePair {
  baselineRow: BoqRow
  targetRow: BoqRow
  similarity: SimilarityBreakdown
  unitExact: boolean
}

function exactFeatureSignature(row: BoqRow): string {
  const { itemName, itemDescription, unit } = row.normalizedFields
  return itemName && unit ? `${itemName}\u001f${itemDescription}\u001f${unit}` : ''
}

function ambiguous(candidates: readonly MatchCandidate[], margin: number): boolean {
  const first = candidates.at(0)
  const second = candidates.at(1)
  return first !== undefined && second !== undefined && first.score - second.score <= margin
}

function sortResults(results: readonly MatchResult[]): MatchResult[] {
  return [...results].sort((left, right) => {
    if (left.baselineRow !== null && right.baselineRow !== null) {
      return (
        left.baselineRow.sourceRowNumber - right.baselineRow.sourceRowNumber ||
        left.baselineRow.id.localeCompare(right.baselineRow.id)
      )
    }
    if (left.baselineRow !== null) return -1
    if (right.baselineRow !== null) return 1
    return (
      (left.targetRow?.sourceRowNumber ?? 0) - (right.targetRow?.sourceRowNumber ?? 0) ||
      (left.targetRow?.id ?? '').localeCompare(right.targetRow?.id ?? '')
    )
  })
}

/** Runs the four transparent matching stages and returns diff-ready results. */
export function matchBoqRows(
  baselineInput: readonly BoqRow[],
  targetInput: readonly BoqRow[],
  options: MatchingOptions = {},
): MatchResult[] {
  const resolved = resolveMatchingOptions(options)
  const baselineRows = sortRows(baselineInput.filter(isComparableRow).map(prepareRow))
  const targetRows = sortRows(targetInput.filter(isComparableRow).map(prepareRow))
  const baselineCodeGroups = groupByCode(baselineRows)
  const targetCodeGroups = groupByCode(targetRows)
  const matchedByBaseline = new Map<string, MatchResult>()
  const usedTargetIds = new Set<string>()
  const assign = (result: MatchResult): void => {
    if (result.baselineRow !== null) matchedByBaseline.set(result.baselineRow.id, result)
    if (result.targetRow !== null) usedTargetIds.add(result.targetRow.id)
  }

  // Stage 1: codes must be non-empty and unique on both complete sides.
  for (const baselineRow of baselineRows) {
    const code = baselineRow.normalizedFields.itemCode
    const baselineGroup = baselineCodeGroups.get(code)
    const targetGroup = targetCodeGroups.get(code)
    if (code && baselineGroup?.length === 1 && targetGroup?.length === 1) {
      const targetRow = targetGroup[0]
      assign(
        createResult(
          baselineRow,
          targetRow,
          'unique-code',
          1,
          '项目编码唯一且一致',
          'automatic',
          [],
          resolved.tolerances,
        ),
      )
    }
  }

  // Stage 2: deterministic greedy pairing, prioritizing equal units and features.
  for (const code of [...baselineCodeGroups.keys()].sort()) {
    const originalBaselineGroup = baselineCodeGroups.get(code) ?? []
    const originalTargetGroup = targetCodeGroups.get(code) ?? []
    if (originalBaselineGroup.length <= 1 && originalTargetGroup.length <= 1) continue
    const baselineGroup = originalBaselineGroup.filter((row) => !matchedByBaseline.has(row.id))
    const targetGroup = originalTargetGroup.filter((row) => !usedTargetIds.has(row.id))
    const pairs: DuplicatePair[] = []
    for (const baselineRow of baselineGroup) {
      for (const targetRow of targetGroup) {
        const similarity = scoreRowSimilarity(baselineRow, targetRow)
        pairs.push({
          baselineRow,
          targetRow,
          similarity,
          unitExact:
            Boolean(baselineRow.normalizedFields.unit) &&
            baselineRow.normalizedFields.unit === targetRow.normalizedFields.unit,
        })
      }
    }
    pairs.sort(
      (left, right) =>
        Number(right.unitExact) - Number(left.unitExact) ||
        right.similarity.itemDescription - left.similarity.itemDescription ||
        right.similarity.itemName - left.similarity.itemName ||
        right.similarity.weightedScore - left.similarity.weightedScore ||
        left.baselineRow.sourceRowNumber - right.baselineRow.sourceRowNumber ||
        left.targetRow.sourceRowNumber - right.targetRow.sourceRowNumber ||
        left.baselineRow.id.localeCompare(right.baselineRow.id) ||
        left.targetRow.id.localeCompare(right.targetRow.id),
    )
    for (const pair of pairs) {
      if (matchedByBaseline.has(pair.baselineRow.id) || usedTargetIds.has(pair.targetRow.id))
        continue
      assign(
        createResult(
          pair.baselineRow,
          pair.targetRow,
          'duplicate-code',
          pair.similarity.weightedScore,
          `重复编码消歧（相似度 ${pair.similarity.weightedScore.toFixed(3)}）`,
          'automatic',
          [],
          resolved.tolerances,
        ),
      )
    }
  }

  // Stage 3: exact name + feature + unit identity, regardless of changed/missing code.
  const exactTargetGroups = new Map<string, BoqRow[]>()
  for (const targetRow of targetRows) {
    if (usedTargetIds.has(targetRow.id)) continue
    const signature = exactFeatureSignature(targetRow)
    if (!signature) continue
    const group = exactTargetGroups.get(signature)
    if (group === undefined) exactTargetGroups.set(signature, [targetRow])
    else group.push(targetRow)
  }
  for (const baselineRow of baselineRows) {
    if (matchedByBaseline.has(baselineRow.id)) continue
    const signature = exactFeatureSignature(baselineRow)
    const candidates = signature ? exactTargetGroups.get(signature) : undefined
    if (candidates === undefined) continue
    const targetRow = candidates
      .filter((row) => !usedTargetIds.has(row.id))
      .sort(
        (left, right) =>
          Number(right.normalizedFields.itemCode === baselineRow.normalizedFields.itemCode) -
            Number(left.normalizedFields.itemCode === baselineRow.normalizedFields.itemCode) ||
          left.sourceRowNumber - right.sourceRowNumber ||
          left.id.localeCompare(right.id),
      )
      .at(0)
    if (targetRow === undefined) continue
    const codeChanged =
      baselineRow.normalizedFields.itemCode !== targetRow.normalizedFields.itemCode
    assign(
      createResult(
        baselineRow,
        targetRow,
        'exact-features',
        1,
        codeChanged ? '项目名称、项目特征和单位一致；项目编码变化' : '项目名称、项目特征和单位一致',
        'automatic',
        [],
        resolved.tolerances,
      ),
    )
  }

  // Stage 4: indexed and capped fuzzy candidates for the remaining rows.
  const remainingTargets = targetRows.filter((row) => !usedTargetIds.has(row.id))
  const candidateIndex = buildCandidateIndex(remainingTargets)
  const searches = new Map<string, readonly MatchCandidate[]>()
  const remainingBaselines = baselineRows.filter((row) => !matchedByBaseline.has(row.id))
  for (const baselineRow of remainingBaselines) {
    searches.set(
      baselineRow.id,
      searchFuzzyCandidates(baselineRow, candidateIndex, {
        maxCandidates: resolved.maxCandidates,
      }).candidates,
    )
  }
  remainingBaselines.sort((left, right) => {
    const leftScore = searches.get(left.id)?.[0]?.score ?? 0
    const rightScore = searches.get(right.id)?.[0]?.score ?? 0
    return (
      rightScore - leftScore ||
      left.sourceRowNumber - right.sourceRowNumber ||
      left.id.localeCompare(right.id)
    )
  })
  for (const baselineRow of remainingBaselines) {
    const available = (searches.get(baselineRow.id) ?? []).filter(
      (candidate) => !usedTargetIds.has(candidate.targetRow.id),
    )
    const top = available.at(0)
    if (top === undefined || top.score < resolved.pendingThreshold) continue
    const needsConfirmation =
      top.score < resolved.highConfidenceThreshold || ambiguous(available, resolved.ambiguityMargin)
    const displayed = available.slice(0, resolved.maxDisplayedCandidates)
    assign(
      createResult(
        baselineRow,
        top.targetRow,
        'fuzzy',
        top.score,
        needsConfirmation
          ? ambiguous(available, resolved.ambiguityMargin)
            ? '存在两个得分非常接近的候选，需人工确认'
            : '模糊匹配得分处于待确认区间'
          : '模糊匹配达到高可信阈值',
        needsConfirmation ? 'pending' : 'automatic',
        displayed,
        resolved.tolerances,
      ),
    )
  }

  const results: MatchResult[] = []
  for (const baselineRow of baselineRows) {
    const matched = matchedByBaseline.get(baselineRow.id)
    results.push(
      matched ??
        createResult(
          baselineRow,
          null,
          'unmatched',
          0,
          '未找到达到待确认阈值的候选，判定为删除',
          'automatic',
          [],
          resolved.tolerances,
        ),
    )
  }
  for (const targetRow of targetRows) {
    if (!usedTargetIds.has(targetRow.id)) {
      results.push(
        createResult(
          null,
          targetRow,
          'unmatched',
          0,
          '未被任何基准版项目匹配，判定为新增',
          'automatic',
          [],
          resolved.tolerances,
        ),
      )
    }
  }

  // Remove candidates claimed by another result while retaining the pending top target.
  const ownerByTarget = new Map<string, string>()
  for (const result of results) {
    if (result.targetRow !== null && result.status !== 'added') {
      ownerByTarget.set(result.targetRow.id, result.id)
    }
  }
  return sortResults(
    results.map((result) =>
      result.status !== 'pending'
        ? result
        : {
            ...result,
            candidates: result.candidates.filter(
              (candidate) =>
                ownerByTarget.get(candidate.targetRow.id) === undefined ||
                ownerByTarget.get(candidate.targetRow.id) === result.id,
            ),
          },
    ),
  )
}

export const runMatching = matchBoqRows

export function createAddedMatchResult(
  targetRow: BoqRow,
  tolerances: Partial<NumericTolerances> = {},
): MatchResult {
  return createResult(
    null,
    prepareRow(targetRow),
    'unmatched',
    0,
    '人工处理后未被匹配，判定为新增',
    'automatic',
    [],
    tolerances,
  )
}

export const sortMatchResults = sortResults
